import React, { useState, useEffect } from 'react';
import { servicesData as fallbackData } from '../mock';
import {
  FileCheck,
  Building,
  BatteryCharging,
  Leaf,
  Volume2,
  Flame,
  CloudRain,
  Gavel,
  CheckCircle,
  Calculator,
  Beaker,
  GitCompare,
  Layers,
  Loader2
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const iconMap = {
  'file-check': FileCheck,
  building: Building,
  'battery-charging': BatteryCharging,
  leaf: Leaf,
  'volume-2': Volume2,
  flame: Flame,
  'cloud-rain': CloudRain,
  gavel: Gavel,
  'check-circle': CheckCircle,
  calculator: Calculator,
  beaker: Beaker,
  'git-compare': GitCompare,
  layers: Layers
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/services`);
        if (!response.ok) {
          throw new Error('Error fetching services');
        }
        const data = await response.json();
        setServices(data);
      } catch (err) {
        console.error('Error loading services:', err);
        setError(err.message);
        // Use fallback data if API fails
        setServices(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return (
    <section id="servicios" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#0A3A48] mb-4">Servicios Especializados</h2>
          <div className="w-24 h-1 bg-[#5E8B7E] mx-auto mb-6"></div>
          <p className="text-xl text-[#4A4A4A] max-w-3xl mx-auto">
            Ofrecemos soluciones técnicas integrales para el desarrollo y certificación de proyectos constructivos
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12" data-testid="services-loading">
            <Loader2 className="animate-spin text-[#5E8B7E]" size={40} />
          </div>
        )}

        {/* Services Accordion */}
        {!loading && (
          <div className="max-w-4xl mx-auto" data-testid="services-list">
            <Accordion type="single" collapsible className="space-y-4">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Building;
                return (
                  <AccordionItem
                    key={service._id || index}
                    value={`item-${index}`}
                    className="border-0 bg-gradient-to-r from-white to-gray-50 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                    data-testid={`service-item-${index}`}
                  >
                    <AccordionTrigger className="px-6 py-4 hover:no-underline">
                      <div className="flex items-center text-left">
                        <div className="p-2 bg-[#5E8B7E]/10 rounded-lg mr-4">
                          <IconComponent className="text-[#5E8B7E]" size={24} />
                        </div>
                        <span className="text-lg font-semibold text-[#0A3A48]">
                          {service.title}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4">
                      <p className="text-[#4A4A4A] leading-relaxed pl-14">
                        {service.description}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
